import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/stores/auth';

function AuthLoading() {
  return (
    <div className="flex h-full items-center justify-center text-makhe-muted">
      Loading…
    </div>
  );
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { initialised, firebaseUser, userDoc } = useAuth();
  const location = useLocation();

  if (!initialised) return <AuthLoading />;
  if (!firebaseUser) {
    // Preserve the intended destination as a query param so it survives the
    // sign-in ⇄ sign-up toggle (and a page reload), then SignIn/SignUp send the
    // user there after auth. This is what makes landing deeplinks work for new
    // users: click a service → sign up → land back on that service.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/sign-in?next=${next}`} replace state={{ from: location }} />;
  }

  // Email verification gate disabled for MVP — no email service configured.
  // Re-enable once SES/SendGrid/Firebase email is wired up.

  // Student verification gate. Verification is required to *transact* (book,
  // post requests, manage payments) but NOT to *browse* — so a freshly
  // signed-up user can still land on a deeplinked service or creator profile
  // and look around before verifying.
  const p = location.pathname;
  const isOnVerifyFlow = p.startsWith('/profile/verify-student');
  const isBrowsable =
    p.startsWith('/service/') ||
    p.startsWith('/seller/') ||
    p === '/feed' ||
    p === '/discover' ||
    p === '/favourites';
  const verificationStatus = userDoc?.studentVerification?.status ?? 'unverified';
  if (
    userDoc &&
    verificationStatus !== 'verified' &&
    !isOnVerifyFlow &&
    !isBrowsable
  ) {
    return <Navigate to="/profile/verify-student" replace />;
  }

  return <>{children}</>;
}

export function SellerRoute({
  children,
  requireSeller = true,
}: {
  children: ReactNode;
  requireSeller?: boolean;
}) {
  const { initialised, firebaseUser, userDoc } = useAuth();
  const isSeller = useAuth((s) => s.isSeller());
  // Wait for the user doc before deciding. Otherwise a hard refresh or a
  // bookmarked/deeplinked Studio page bounces a real seller to onboarding while
  // userDoc is still loading (isSeller() is false until the snapshot arrives) —
  // matching AdminRoute, which already guards on userDoc.
  if (!initialised || (firebaseUser && !userDoc)) return <AuthLoading />;
  if (requireSeller && !isSeller) {
    return <Navigate to="/seller/onboarding" replace />;
  }
  return <>{children}</>;
}

export function AdminRoute({ children }: { children: ReactNode }) {
  const { initialised, userDoc } = useAuth();
  const isAdmin = useAuth((s) => s.isAdmin());

  if (!initialised || !userDoc) return <AuthLoading />;
  if (!isAdmin) return <Navigate to="/feed" replace />;
  return <>{children}</>;
}
